/* eslint-disable no-restricted-syntax */

import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { createRouter, actionToUrl } from '@respond-framework/rudy';
import { isServer } from '@respond-framework/utils';
import createRestoreScroll from '@respond-framework/scroll-restorer';
import { composeWithDevTools as composeWithDebugDevtools } from 'redux-devtools-extension';
import { composeWithDevTools as composeWithProdDevtools } from 'redux-devtools-extension/logOnly';
import { composeWithDevTools as remoteComposeWithDevTools } from 'remote-redux-devtools';
import detectBrowserLanguage from 'detect-browser-language';
import { makeReducer } from './RootReducer';
import { multiDomainRoutesMap } from './routesMap';
import { shouldReload } from './shouldReload';
import { makeRoutesMapForOrigin, querySerializer } from './routesHelpers';
import { makeSelectOriginForAction } from './Institutions/CustomDomains/selectors';
import { institutionPrefixMiddleware } from './institutionPrefixMiddleware';
import { CUSTOM_DOMAIN_INSTITUTIONS } from './Institutions/constants';
import { shouldUpdateScroll } from './shouldUpdateScroll';
import { metrics } from '../web/metrics/metrics';
import { actionTriggerRoutes } from './actionTriggers';
import { DEBUG, SSR } from './location/constants';
import { userpilot } from '../web/userpilot/userpilot';
import { OL_LANGUAGE_KEY } from '../web/i18n/constants';

// TODO: Twig: If you can think of a better way of writing this, please let me know.
// https://github.com/zalmoxisus/redux-devtools-extension#12-advanced-store-setup
let composeEnhancers;
if (DEBUG) {
  if (SSR) {
    composeEnhancers = remoteComposeWithDevTools({
      realtime: true,
      hostname: 'localhost',
      port: 8007,
    });
  } else {
    composeEnhancers = composeWithDebugDevtools;
  }
} else {
  composeEnhancers = composeWithProdDevtools;
}

// A list of current instances of stores (for HMR reducer replacement)
const storeInstances = [];

export const createStoreCommon = (
  previousInjectedReducers,
  initialState,
  currentOrigin,
  rudyOptions,
  rudyMiddlewares
) => {
  const storeInstance = {
    injectedReducers: previousInjectedReducers || {},
    store: undefined,
    refreshReducer: undefined,
    reloadStore: undefined,
    addReducer: undefined,
    destroy: undefined,
    firstRoute: undefined,
  };

  // Function to be called to add a new async loaded reducer
  storeInstance.addReducer = (name, reducer) => {
    storeInstance.injectedReducers[name] = reducer;
    storeInstance.refreshReducer();
  };

  const currentOriginRoutesMap = makeRoutesMapForOrigin(
    multiDomainRoutesMap,
    currentOrigin
  );

  const {
    reducer: routerReducer,
    middleware: routerMiddleware,
    firstRoute,
    api: rudyApi,
  } = createRouter(
    {
      ...currentOriginRoutesMap,
      /**
       * actionTriggerRoutes is a set of pathless thunks compatible with Rudy.
       * They are not URL routes, but simply callbacks called in response to the
       * the dispatch of certain types of redux action.
       */
      ...actionTriggerRoutes,
    },
    {
      ...rudyOptions,
      ...querySerializer,
      querySerializer,
      beforeEnter: ({ getState, action }) => {
        const languageSet = window.localStorage.getItem(OL_LANGUAGE_KEY);
        if (!languageSet) {
          const language = detectBrowserLanguage();
          window.localStorage.setItem(OL_LANGUAGE_KEY, language);
        }

        // Ideally redirects should set location.kind === 'redirect' ... but they don't?
        const isRedirect = action.location.status === 302;
        if (!isServer() && (action.location.kind !== 'load' || isRedirect)) {
          // Reloading the page when necessary
          const destinationOrigin = makeSelectOriginForAction(action)(
            getState()
          );
          if (destinationOrigin !== currentOrigin) {
            // Woe is us, we must reboot because the destination is on a different domain
            const destinationRoutesMap = makeRoutesMapForOrigin(
              multiDomainRoutesMap,
              destinationOrigin
            );
            const destinationPath = actionToUrl(action, {
              ...rudyApi,
              routes: destinationRoutesMap,
            }).url;
            window.location.assign(`${destinationOrigin}${destinationPath}`);
            // Block the thunk by returning a promise which never resolves
            return new Promise(() => {});
          } else if (shouldReload(getState, action)) {
            // Oh no, it seems we need to reboot to postpone refactoring some legacy code.
            window.location.assign(actionToUrl(action, rudyApi).url);
            // Block the thunk by returning a promise which never resolves
            return new Promise(() => {});
          }
        }
        return Promise.resolve();
      },
      extra: {
        addReducer: storeInstance.addReducer,
        metrics,
        userpilot,
        ...rudyOptions.extra,
      },
      restoreScroll: createRestoreScroll({
        shouldUpdateScroll,
      }),
    },
    rudyMiddlewares
  );

  const makeReducerWithInjected = () =>
    makeReducer(routerReducer, storeInstance.injectedReducers);

  const createStoreWithMiddleware = composeEnhancers(
    applyMiddleware(
      institutionPrefixMiddleware(
        CUSTOM_DOMAIN_INSTITUTIONS,
        currentOriginRoutesMap
      ),
      thunk,
      routerMiddleware
    )
  )(createStore);

  storeInstance.store = createStoreWithMiddleware(
    makeReducerWithInjected(),
    initialState
  );

  storeInstance.destroy = () => {
    storeInstances.splice(storeInstances.indexOf(storeInstance), 1);
  };

  storeInstance.refreshReducer = () => {
    storeInstance.store.replaceReducer(makeReducerWithInjected());
  };

  storeInstance.firstRoute = firstRoute;

  storeInstance.thunk = thunk;

  storeInstance.rudyApi = rudyApi;

  storeInstances.push(storeInstance);

  return storeInstance;
};

if (module.hot) {
  // These will just take effect on the next route action dispatched
  module.hot.accept(['./shouldReload', './location/constants']);

  // If the root reducer changes, the stores' reducers can be replaced
  module.hot.accept('./RootReducer', () => {
    for (const storeInstance of storeInstances) {
      storeInstance.refreshReducer();
    }
  });
}
